import { supabase } from '../lib/supabase';
import { automationRulesService } from './automationRulesService';

/**
 * Contract Auto-Routing Service
 * Automatically routes contracts based on type, value, and approval rules
 * with conditional workflows and priority escalation
 */

export const contractAutoRoutingService = {
  /**
   * Auto-route a contract based on rules
   */
  async autoRouteContract(contractId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch contract data
      const { data: contract, error: contractError } = await supabase
        ?.from('contracts')
        ?.select('*, properties(*), clients(*)')
        ?.eq('id', contractId)
        ?.single();

      if (contractError) throw contractError;
      if (!contract) throw new Error('Contract not found');

      // Get active routing rules sorted by priority
      const rules = await automationRulesService?.getAllRules({
        isActive: true,
        triggerType: 'contract_created'
      });

      // Evaluate rules and execute actions
      const routingResults = [];
      for (const rule of rules) {
        const conditionsMet = this.evaluateRoutingConditions(
          rule?.conditions,
          contract
        );

        if (conditionsMet) {
          // Execute routing actions
          const actionResults = await this.executeRoutingActions(
            rule?.actions,
            contract,
            user?.id
          );

          routingResults?.push({
            ruleId: rule?.id,
            ruleName: rule?.ruleName,
            priority: rule?.priority,
            actionsExecuted: actionResults
          });

          // Log execution
          await this.logRuleExecution(
            rule?.id,
            contractId,
            'contract',
            true,
            actionResults
          );

          // Stop after first matching rule (highest priority)
          break;
        }
      }

      // If no rules matched, apply default routing
      if (routingResults?.length === 0) {
        const defaultRouting = await this.applyDefaultRouting(contract, user?.id);
        routingResults?.push(defaultRouting);
      }

      return {
        success: true,
        contractId,
        routingResults,
        message: 'تم توجيه العقد تلقائياً بنجاح'
      };
    } catch (error) {
      console.error('Error in auto-routing:', error);
      throw error;
    }
  },

  /**
   * Evaluate routing conditions
   */
  evaluateRoutingConditions(conditions, contract) {
    if (!conditions || conditions?.length === 0) return true;

    let result = true;
    let currentLogicalOp = 'AND';

    for (const condition of conditions) {
      const conditionMet = this.evaluateSingleCondition(
        condition,
        contract
      );

      if (currentLogicalOp === 'AND') {
        result = result && conditionMet;
      } else if (currentLogicalOp === 'OR') {
        result = result || conditionMet;
      }

      currentLogicalOp = condition?.logicalOperator || 'AND';
    }

    return result;
  },

  /**
   * Evaluate single condition
   */
  evaluateSingleCondition(condition, contract) {
    const field = condition?.field;
    const operator = condition?.operator;
    const expectedValue = condition?.value;

    // Get actual value from contract
    let actualValue;
    if (field === 'contract_type') {
      actualValue = contract?.contract_type;
    } else if (field === 'contract_value' || field === 'amount') {
      actualValue = parseFloat(contract?.amount) || 0;
    } else if (field === 'property_type') {
      actualValue = contract?.properties?.property_type;
    } else if (field === 'client_segment' || field === 'client_category') {
      actualValue = contract?.clients?.category;
    } else if (field === 'status') {
      actualValue = contract?.status;
    } else {
      actualValue = contract?.[field];
    }

    // Evaluate based on operator
    switch (operator) {
      case 'equals':
        return actualValue === expectedValue;
      case 'not_equals':
        return actualValue !== expectedValue;
      case 'greater_than':
        return parseFloat(actualValue) > parseFloat(expectedValue);
      case 'less_than':
        return parseFloat(actualValue) < parseFloat(expectedValue);
      case 'contains':
        return String(actualValue)?.toLowerCase()?.includes(String(expectedValue)?.toLowerCase());
      case 'in_list':
        return Array.isArray(expectedValue) && expectedValue?.includes(actualValue);
      default:
        return false;
    }
  },

  /**
   * Execute routing actions
   */
  async executeRoutingActions(actions, contract, userId) {
    const results = [];

    for (const action of actions) {
      try {
        let actionResult;

        switch (action?.type) {
          case 'assign_contract_type':
            actionResult = await this.assignContractType(
              contract,
              action?.config
            );
            break;

          case 'assign_to_user':
            actionResult = await this.assignToUser(
              contract,
              action?.config,
              userId
            );
            break;

          case 'set_priority':
            actionResult = await this.setPriority(
              contract,
              action?.config
            );
            break;

          case 'send_notification':
            actionResult = await this.sendRoutingNotification(
              contract,
              action?.config,
              userId
            );
            break;

          case 'schedule_followup':
            actionResult = await this.scheduleFollowup(
              contract,
              action?.config,
              userId
            );
            break;

          case 'auto_approve':
            actionResult = await this.autoApprove(
              contract,
              action?.config,
              userId
            );
            break;

          default:
            actionResult = { success: false, message: 'Unknown action type' };
        }

        results?.push({
          actionType: action?.type,
          success: actionResult?.success,
          message: actionResult?.message
        });
      } catch (error) {
        console.error(`Error executing action ${action?.type}:`, error);
        results?.push({
          actionType: action?.type,
          success: false,
          error: error?.message
        });
      }
    }

    return results;
  },

  /**
   * Assign contract type
   */
  async assignContractType(contract, config) {
    try {
      const { error } = await supabase
        ?.from('contracts')
        ?.update({
          contract_type: config?.contract_type || config?.contractType
        })
        ?.eq('id', contract?.id);

      if (error) throw error;

      return {
        success: true,
        message: `تم تعيين نوع العقد إلى: ${config?.contract_type || config?.contractType}`
      };
    } catch (error) {
      return { success: false, message: error?.message };
    }
  },

  /**
   * Assign to user based on role or specific user
   */
  async assignToUser(contract, config, currentUserId) {
    try {
      let assignedUserId;

      if (config?.userId) {
        // Specific user assignment
        assignedUserId = config?.userId;
      } else if (config?.role) {
        // Role-based assignment
        const { data: users } = await supabase
          ?.from('user_profiles')
          ?.select('id')
          ?.eq('role', config?.role)
          ?.eq('is_active', true)
          ?.limit(1);

        if (users && users?.length > 0) {
          assignedUserId = users?.[0]?.id;
        } else {
          assignedUserId = currentUserId; // Fallback to current user
        }
      } else {
        assignedUserId = currentUserId;
      }

      const { error } = await supabase
        ?.from('contracts')
        ?.update({ user_id: assignedUserId })
        ?.eq('id', contract?.id);

      if (error) throw error;

      return {
        success: true,
        message: `تم تعيين العقد للمستخدم`,
        assignedUserId
      };
    } catch (error) {
      return { success: false, message: error?.message };
    }
  },

  /**
   * Set priority level
   */
  async setPriority(contract, config) {
    try {
      // Create alert with priority
      const { data: { user } } = await supabase?.auth?.getUser();
      
      const { error } = await supabase
        ?.from('alerts')
        ?.insert({
          user_id: contract?.user_id || user?.id,
          title: `عقد ذو أولوية ${config?.priority === 'high' ? 'عالية' : config?.priority === 'medium' ? 'متوسطة' : 'منخفضة'}`,
          description: `العقد رقم ${contract?.contract_number} يتطلب انتباه ${config?.priority === 'high' ? 'فوري' : 'عادي'}`,
          category: 'contract_priority',
          priority: config?.priority || 'medium',
          is_read: false
        });

      if (error) throw error;

      return {
        success: true,
        message: `تم تعيين الأولوية: ${config?.priority}`
      };
    } catch (error) {
      return { success: false, message: error?.message };
    }
  },

  /**
   * Send routing notification via email
   */
  async sendRoutingNotification(contract, config, userId) {
    try {
      // Get user email
      const { data: userProfile } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name')
        ?.eq('id', userId)
        ?.single();

      if (!userProfile?.email) {
        return { success: false, message: 'User email not found' };
      }

      // Call Resend edge function
      const { data, error } = await supabase?.functions?.invoke('send-contract-routing-email', {
        body: {
          to: userProfile?.email,
          recipientName: userProfile?.full_name,
          contractNumber: contract?.contract_number,
          contractType: contract?.contract_type,
          contractAmount: contract?.amount,
          priority: config?.priority || 'medium',
          message: config?.message || 'تم توجيه عقد جديد إليك'
        }
      });

      if (error) throw error;

      return {
        success: true,
        message: 'تم إرسال إشعار البريد الإلكتروني بنجاح'
      };
    } catch (error) {
      console.error('Error sending routing notification:', error);
      return { success: false, message: error?.message };
    }
  },

  /**
   * Schedule follow-up
   */
  async scheduleFollowup(contract, config, userId) {
    try {
      const daysAhead = config?.days_ahead || config?.daysAhead || 3;
      const followupDate = new Date();
      followupDate?.setDate(followupDate?.getDate() + daysAhead);

      const { error } = await supabase
        ?.from('scheduled_tasks')
        ?.insert({
          user_id: userId,
          task_name: config?.message || `متابعة العقد ${contract?.contract_number}`,
          task_type: 'contract_followup',
          frequency: 'once',
          schedule_config: {
            contractId: contract?.id,
            contractNumber: contract?.contract_number,
            followupDate: followupDate?.toISOString()
          },
          is_active: true,
          next_run_at: followupDate?.toISOString()
        });

      if (error) throw error;

      return {
        success: true,
        message: `تم جدولة متابعة بعد ${daysAhead} أيام`
      };
    } catch (error) {
      return { success: false, message: error?.message };
    }
  },

  /**
   * Auto-approve contract
   */
  async autoApprove(contract, config, userId) {
    try {
      // Update contract status
      const { error: contractError } = await supabase
        ?.from('contracts')
        ?.update({ status: 'approved' })
        ?.eq('id', contract?.id);

      if (contractError) throw contractError;

      // Create approval record
      const { error: approvalError } = await supabase
        ?.from('contract_approvals')
        ?.insert({
          contract_id: contract?.id,
          approver_id: userId,
          approver_name: 'نظام الموافقة التلقائية',
          approver_role: 'system',
          action: 'approved',
          comments: config?.reason || 'تمت الموافقة تلقائياً بناءً على قواعد التوجيه'
        });

      if (approvalError) throw approvalError;

      return {
        success: true,
        message: 'تمت الموافقة على العقد تلقائياً'
      };
    } catch (error) {
      return { success: false, message: error?.message };
    }
  },

  /**
   * Apply default routing when no rules match
   */
  async applyDefaultRouting(contract, userId) {
    try {
      // Default: assign to current user with medium priority
      const { error } = await supabase
        ?.from('alerts')
        ?.insert({
          user_id: userId,
          title: 'عقد جديد يتطلب المراجعة',
          description: `العقد رقم ${contract?.contract_number} - ${contract?.contract_type}`,
          category: 'contract_review',
          priority: 'medium',
          is_read: false
        });

      if (error) throw error;

      return {
        ruleId: null,
        ruleName: 'التوجيه الافتراضي',
        priority: 0,
        actionsExecuted: [{
          actionType: 'default_assignment',
          success: true,
          message: 'تم تعيين العقد للمراجعة اليدوية'
        }]
      };
    } catch (error) {
      console.error('Error applying default routing:', error);
      return {
        ruleId: null,
        ruleName: 'التوجيه الافتراضي',
        priority: 0,
        actionsExecuted: [{
          actionType: 'default_assignment',
          success: false,
          error: error?.message
        }]
      };
    }
  },

  /**
   * Log rule execution
   */
  async logRuleExecution(ruleId, entityId, entityType, conditionsMet, actionsExecuted) {
    try {
      const { error } = await supabase
        ?.from('rule_execution_log')
        ?.insert({
          rule_id: ruleId,
          trigger_entity_id: entityId,
          trigger_entity_type: entityType,
          conditions_met: conditionsMet,
          actions_executed: actionsExecuted,
          execution_result: conditionsMet ? 'success' : 'conditions_not_met'
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error logging rule execution:', error);
    }
  },

  /**
   * Priority escalation mechanism
   */
  async escalateContract(contractId, reason = 'timeout') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch contract
      const { data: contract } = await supabase
        ?.from('contracts')
        ?.select('*, user_profiles!contracts_user_id_fkey(email, full_name, role)')
        ?.eq('id', contractId)
        ?.single();

      if (!contract) throw new Error('Contract not found');

      // Find supervisor or manager to escalate to
      const { data: escalationTarget } = await supabase
        ?.from('user_profiles')
        ?.select('id, email, full_name, role')
        ?.in('role', ['manager', 'admin'])
        ?.eq('is_active', true)
        ?.limit(1)
        ?.single();

      if (!escalationTarget) {
        throw new Error('No escalation target found');
      }

      // Create high-priority alert
      const { error: alertError } = await supabase
        ?.from('alerts')
        ?.insert({
          user_id: escalationTarget?.id,
          title: `تصعيد عقد - ${contract?.contract_number}`,
          description: `العقد يتطلب انتباه فوري. السبب: ${reason === 'timeout' ? 'تجاوز الوقت المحدد' : reason}`,
          category: 'contract_escalation',
          priority: 'high',
          is_read: false
        });

      if (alertError) throw alertError;

      // Send escalation email
      await supabase?.functions?.invoke('send-contract-escalation-email', {
        body: {
          to: escalationTarget?.email,
          recipientName: escalationTarget?.full_name,
          contractNumber: contract?.contract_number,
          contractType: contract?.contract_type,
          reason: reason === 'timeout' ? 'تجاوز الوقت المحدد للمراجعة' : reason,
          originalAssignee: contract?.user_profiles?.full_name
        }
      });

      return {
        success: true,
        message: 'تم تصعيد العقد بنجاح',
        escalatedTo: escalationTarget?.full_name
      };
    } catch (error) {
      console.error('Error escalating contract:', error);
      throw error;
    }
  },

  /**
   * Get routing history for a contract
   */
  async getRoutingHistory(contractId) {
    try {
      const { data, error } = await supabase
        ?.from('rule_execution_log')
        ?.select(`
          *,
          automation_rules(rule_name, description, priority)
        `)
        ?.eq('trigger_entity_id', contractId)
        ?.eq('trigger_entity_type', 'contract')
        ?.order('executed_at', { ascending: false });

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        ruleName: log?.automation_rules?.rule_name,
        description: log?.automation_rules?.description,
        priority: log?.automation_rules?.priority,
        conditionsMet: log?.conditions_met,
        actionsExecuted: log?.actions_executed,
        executionResult: log?.execution_result,
        executedAt: log?.executed_at
      }));
    } catch (error) {
      console.error('Error fetching routing history:', error);
      throw error;
    }
  }
};
